import {
  Point2D,
  Point3D,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../../../team-apis/otterdam/calibration-data';
import { H3Material } from '../../../materials/basic/h3-basic-material';
import { getCameraWorldDirection } from '../../calibration-utils';

export interface CylindricalUniforms {
  principlePoint: Point2D;
  focalLength: Point2D;
  imageSize: Point2D;
  cameraWorldDirection: Point3D;
  cameraWorldPosition: Point3D;
}

export function setCylindricalUniforms(
  material: H3Material,
  config: {
    currentFrameCalibrationData: SingleFrameCalibrationData;
  },
) {
  const { focalLength, principlePoint, position, rotation } =
    config.currentFrameCalibrationData;
  material.setUniform('principlePoint', principlePoint);
  material.setUniform('focalLength', focalLength);
  material.setUniform('cameraWorldPosition', position);
  material.setUniform(
    'cameraWorldDirection',
    getCameraWorldDirection(rotation),
  );
}

export const commonCylindrical = `
#if defined ( USE_CYLINDRICAL_PROJECTION ) || defined ( USE_DEFORMED_CYLINDRICAL_PROJECTION )

  // Original formula provided by DBE and CDI
  //    u = principal_point_x - (focal_length_x * atan2(y, x))
  //    v = principal_point_y - ((focal_length_y * z) / sqrt(x^2+y^2))

  // Note that we are using different axis. Client uses the ISO8855 coordinate system
  // where x-axis points forward, y-axis to the left and z-axis upwards. This
  // is different from the OpenGL coordinate system which uses a right handed system
  // https://learnopengl.com/Getting-started/Coordinate-Systems

  // right handed to ISO8855
  // y = z
  // z = -x
  // x = -y

  vec2 lightRayToPixelCylinder(vec3 h3Position, mat4 h3ViewMatrix) {
    vec4 vertexInCamera = h3ViewMatrix * modelMatrix * vec4(h3Position, 1.0);

    float x = vertexInCamera.x;
    float y = vertexInCamera.y;
    float z = vertexInCamera.z;

    float u = principlePoint.x - focalLength.x * atan(x, -z);
    float v = principlePoint.y - (focalLength.y * y) / length(vec2(z, x));

    return vec2(u, v);
  }

#endif
`;

export const cylindricalProjection = `
#if defined ( USE_CYLINDRICAL_PROJECTION )

  vec4 getCylindricalPosition(vec3 h3Position, mat4 h3ViewMatrix) {
    vec4 vertexWorldPosition = modelMatrix * vec4(h3Position, 1.0 );
    vec2 pixel = lightRayToPixelCylinder(h3Position, h3ViewMatrix);

    // Client considers (0, 0) of the cylindrical image as the bottom left
    // corner, while we consider it the top left corner.
    vec2 finalPoint = (imageSize - pixel) / imageSize * 2.0 - 1.0;

    vec3 cameraToPointDirection = normalize(vertexWorldPosition.xyz - cameraWorldPosition);

    float behindCameraTolerance = -0.1;
    if(dot(cameraWorldDirection, cameraToPointDirection) < behindCameraTolerance){
      return vec4(finalPoint, -2.0, 1.0);
    }

    return vec4(finalPoint, 1.0, 1.0);
  }

#endif
`;
